import React from 'react';
import { Semester } from '../types/grades';
import { Award, BookOpen, GraduationCap } from 'lucide-react';
import { useUniversity } from '../contexts/UniversityContext';
import { calculateCumulativeGPA } from '../utils/calculations';
import { Card } from './layout/Card';

interface GPASummaryProps {
  semesters: Semester[];
}

export function GPASummary({ semesters }: GPASummaryProps) {
  const { selectedUniversity } = useUniversity();
  const overallGPA = calculateCumulativeGPA(semesters, selectedUniversity);
  const totalCredits = semesters.reduce((sum, semester) => 
    sum + semester.courses.reduce((credits, course) => credits + course.credits, 0), 0
  );
  const totalCourses = semesters.reduce((sum, semester) => 
    sum + semester.courses.length, 0
  );

  const getGPAColor = (gpa: number): string => {
    if (gpa >= 3.5) return 'text-emerald-600';
    if (gpa >= 3.0) return 'text-blue-600';
    if (gpa >= 2.5) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getAcademicStanding = (gpa: number): string => {
    const scale = selectedUniversity.gradeScale;
    const maxGPA = Math.max(...scale.map(g => g.points));
    const threshold = maxGPA * 0.875;

    if (gpa >= threshold) return 'Excellent';
    if (gpa >= threshold * 0.875) return 'Very Good';
    if (gpa >= threshold * 0.75) return 'Good';
    if (gpa >= threshold * 0.625) return 'Satisfactory';
    return 'Needs Improvement';
  };

  return (
    <Card>
      <h2 className="text-xl font-bold mb-6 text-gray-800 flex items-center gap-2">
        <Award className="text-blue-600" />
        Overall Academic Progress
      </h2>
      
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-gray-50 rounded-lg p-4 text-center">
          <div className="flex items-center justify-center mb-2">
            <GraduationCap className="text-blue-600" size={24} />
          </div>
          <p className="text-sm text-gray-600 mb-1">Overall GPA</p>
          <p className={`text-3xl font-bold ${getGPAColor(overallGPA)}`}>
            {overallGPA.toFixed(2)}
          </p>
        </div>

        <div className="bg-gray-50 rounded-lg p-4 text-center">
          <div className="flex items-center justify-center mb-2">
            <BookOpen className="text-blue-600" size={24} />
          </div>
          <p className="text-sm text-gray-600 mb-1">Total Credits</p>
          <p className="text-3xl font-bold text-gray-800">
            {totalCredits}
          </p>
        </div>

        <div className="bg-gray-50 rounded-lg p-4 text-center">
          <div className="flex items-center justify-center mb-2">
            <Award className="text-blue-600" size={24} />
          </div>
          <p className="text-sm text-gray-600 mb-1">Total Courses</p>
          <p className="text-3xl font-bold text-gray-800">
            {totalCourses}
          </p>
        </div>
      </div>

      <div className="mt-6 pt-6 border-t border-gray-200">
        <div className="grid grid-cols-2 gap-4 text-sm text-gray-600">
          <div>
            <p className="mb-1">Academic Standing:</p>
            <p className={`font-semibold ${getGPAColor(overallGPA)}`}>
              {getAcademicStanding(overallGPA)}
            </p>
          </div>
          <div>
            <p className="mb-1">Total Semesters:</p>
            <p className="font-semibold text-gray-800">{semesters.length}</p>
          </div>
        </div>
      </div>
    </Card>
  );
}